import * as React from "react";
import Header from "../components/common/Header";
import Footer from "../components/common/Footer";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import SettingsIcon from "@mui/icons-material/Settings";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import axios from "axios";
import liff from "@line/liff";

function App() {
  const [motherLanguage, setMotherLanguage] = React.useState("");
  const [studyingLanguage, setStudyingLanguage] = React.useState("");
  const [expireDate, setExpireDate] = React.useState("");
  const [disabled, setDisabled] = React.useState(true);

  const handleMotherLanguageChange = (event: SelectChangeEvent) => {
    setMotherLanguage(event.target.value as string);
    setDisabled(false);
  };
  const handleStudyingLanguageChange = (event: SelectChangeEvent) => {
    setStudyingLanguage(event.target.value as string);
    setDisabled(false);
  };

  const submitLanguages = async () => {
    setDisabled(true);
    const profile = await liff.getProfile();
    axios
      .post("v1/api/mypage/update", {
        motherLanguage: motherLanguage,
        studyingLanguage: studyingLanguage,
        userId: profile.userId,
      })
      .then(() => {
        setDisabled(false);
        alert("更新しました");
      });
  };

  React.useEffect(() => {
    const fetchMypage = async () => {
      try {
        const profile = await liff.getProfile();
        const response = await axios.post("/v1/api/mypage/fetch", {
          userId: profile.userId,
        });

        setMotherLanguage(response.data.Items[0].mother_language);
        setStudyingLanguage(response.data.Items[0].studying_language);
        setExpireDate(response.data.Items[0].expire_date);
      } catch (error) {
        console.error("Error set Languages:", error);
      }
    };

    fetchMypage();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f1f5f9", display: "flex", flexDirection: "column" }}>
      <Box sx={{ width: "100%", position: "sticky", top: "0", zIndex: 100 }}>
        <Header />
      </Box>

      <Box sx={{ maxWidth: 560, mx: "auto", px: 2, pt: 4, pb: 2, flex: 1, width: "100%" }}>
        {/* Page heading */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #4338ca, #6366f1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(99,102,241,0.4)",
            }}
          >
            <SettingsIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.4rem",
                color: "#1e293b",
                lineHeight: 1.2,
              }}
            >
              MyPage
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>
              アカウント設定
            </Typography>
          </Box>
        </Box>

        {/* Contract expire date card */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 2.5,
            mb: 2.5,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            border: "1px solid rgba(226,232,240,0.8)",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #0f766e, #14b8a6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CalendarTodayIcon sx={{ color: "white", fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 500 }}>
              契約終了日
            </Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#1e293b" }}>
              {expireDate || "—"}
            </Typography>
          </Box>
        </Box>

        {/* Language settings card */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 2.5,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            border: "1px solid rgba(226,232,240,0.8)",
          }}
        >
          <Typography
            sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#475569", mb: 2.5 }}
          >
            言語設定
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel
              id="mother-language-id"
              shrink={Boolean(motherLanguage)}
              sx={{ fontSize: "0.9rem" }}
            >
              母国語 (Mother Tongue)
            </InputLabel>
            <Select
              labelId="mother-language-id"
              id="mother-language"
              value={motherLanguage}
              label="母国語 (Mother Tongue)"
              notched={Boolean(motherLanguage)}
              onChange={handleMotherLanguageChange}
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value={"ja"}>日本語</MenuItem>
              <MenuItem value={"en"}>English</MenuItem>
              <MenuItem value={"fr"}>Français</MenuItem>
              <MenuItem value={"de"}>German</MenuItem>
              <MenuItem value={"es"}>Spanish</MenuItem>
              <MenuItem value={"it"}>Italian</MenuItem>
              <MenuItem value={"ko"}>Korean</MenuItem>
              <MenuItem value={"zh"}>Chinese</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel
              id="target-language-id"
              shrink={Boolean(studyingLanguage)}
              sx={{ fontSize: "0.9rem" }}
            >
              勉強中の言語 (Studying Language)
            </InputLabel>
            <Select
              labelId="target-language-id"
              id="target-language"
              value={studyingLanguage}
              label="勉強中の言語 (Studying Language)"
              notched={Boolean(studyingLanguage)}
              onChange={handleStudyingLanguageChange}
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value={"en-US"}>English (American)</MenuItem>
              <MenuItem value={"en-GB"}>English (UK)</MenuItem>
              <MenuItem value={"en-AU"}>English (Australia)</MenuItem>
              <MenuItem value={"fr-FR"}>Français</MenuItem>
              <MenuItem value={"de-DE"}>German</MenuItem>
              <MenuItem value={"es-ES"}>Spanish</MenuItem>
              <MenuItem value={"it-IT"}>Italian</MenuItem>
              <MenuItem value={"ko-KR"}>Korean</MenuItem>
              <MenuItem value={"zh-CN"}>Chinese (China)</MenuItem>
              <MenuItem value={"ja-JP"}>日本語</MenuItem>
            </Select>
          </FormControl>

          <Button
            disabled={disabled}
            onClick={submitLanguages}
            fullWidth
            sx={{
              background: disabled
                ? undefined
                : "linear-gradient(135deg, #4338ca, #6366f1)",
              color: disabled ? undefined : "white",
              borderRadius: "10px",
              py: 1.5,
              fontWeight: 600,
              fontSize: "0.95rem",
              textTransform: "none",
              boxShadow: disabled ? "none" : "0 4px 14px rgba(99,102,241,0.35)",
              "&:hover": {
                background: disabled
                  ? undefined
                  : "linear-gradient(135deg, #3730a3, #4f46e5)",
              },
            }}
          >
            更新する
          </Button>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}

export default App;
